import { Fragment, SVGProps, useEffect, useState } from "react";

import { CheckIcon } from "@heroicons/react/24/outline";

import classNames from "../../utils/classNames";
import { IVehicle } from "../../models/Vehicle";
import RowCard from "./RowCard";

// Heroicons only ships `TruckIcon`, which reads as a lorry and feels wrong
// next to the Vehicle Info title. This inline SVG is a small hatchback/saloon
// profile at the same 24×24 viewport as the rest of the RowCard icons so it
// drops in without any layout adjustments.
const CarIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.4 6.6a2 2 0 0 1 1.73-1h7.74a2 2 0 0 1 1.73 1l1.9 3.3A3 3 0 0 1 22 13v3.5a1.5 1.5 0 0 1-1.5 1.5H19a2 2 0 0 1-4 0H9a2 2 0 0 1-4 0H3.5A1.5 1.5 0 0 1 2 16.5V13a3 3 0 0 1 2.5-2.96Zm1.73.5a.5.5 0 0 0-.43.25L6.2 10h11.6l-1.5-2.65a.5.5 0 0 0-.43-.25H8.13ZM6 14.5a1 1 0 1 0 2 0 1 1 0 0 0-2 0Zm11 0a1 1 0 1 0 2 0 1 1 0 0 0-2 0Z"
    />
  </svg>
);

function CopyText({
  text,
  className = "",
  title,
}: {
  text: string | number | null | undefined;
  className?: string;
  title?: string;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1200);
    return () => clearTimeout(t);
  }, [copied]);

  if (text === null || text === undefined || text === "") return null;

  const value = String(text);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard
      .writeText(value)
      .then(() => setCopied(true))
      .catch((err) => console.error("Failed to copy:", err));
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={title ?? (copied ? "Copied!" : "Click to copy")}
      aria-live="polite"
      className={classNames(
        "inline-flex items-center gap-1 rounded px-1 -mx-1 text-left transition-all duration-150 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-400",
        copied
          ? "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200"
          : "hover:bg-slate-100 hover:text-primary-600",
        className
      )}
    >
      {copied ? (
        <>
          <CheckIcon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
          <span className="font-medium">Copied!</span>
        </>
      ) : (
        <span className="truncate">{value}</span>
      )}
    </button>
  );
}

interface VehicleInfoCellProps {
  vehicle: IVehicle;
}

// Mirrors the Stock page `DonorVehicleCell` layout so the awaiting-inventory
// table feels consistent with the rest of the parts area. A compact two-column
// definition list with copy affordances for Reg / VIN / Engine Code.
export default function VehicleInfoCell({ vehicle }: VehicleInfoCellProps) {
  if (!vehicle) {
    return <span className="text-xs text-slate-400">—</span>;
  }

  const mileage =
    typeof vehicle.odometerInMiles === "number"
      ? `${vehicle.odometerInMiles.toLocaleString("en-GB")} mi`
      : null;

  const bodyParts = [
    vehicle.numberOfDoors ? `${vehicle.numberOfDoors}dr` : null,
    vehicle.bodyStyle || vehicle.bodyStyleDescription || null,
  ].filter(Boolean);
  const body = bodyParts.length > 0 ? bodyParts.join(" ") : null;

  const fuel =
    vehicle.combinedFuelType ||
    vehicle.fuelType ||
    vehicle.fuelDescription ||
    null;
  const transmission =
    vehicle.combinedTransmission ||
    vehicle.dvlaTransmission ||
    vehicle.transmission ||
    null;
  const bhp =
    typeof vehicle.maximumPowerBHP === "number"
      ? `${vehicle.maximumPowerBHP} bhp`
      : null;

  const year = vehicle.dvlaYearOfManufacture ?? null;

  const vin = vehicle.combinedVIN || vehicle.vinOriginalDVLA || null;

  const fields: { label: string; value: React.ReactNode | null }[] = [
    {
      label: "Reg",
      value: vehicle.vrm ? <CopyText text={vehicle.vrm} /> : null,
    },
    {
      label: "VIN",
      value: vin ? <CopyText text={vin} /> : null,
    },
    { label: "Year", value: year },
    {
      label: "Make",
      value: vehicle.combinedMake || vehicle.dvlaMake || null,
    },
    {
      label: "Model",
      value: vehicle.combinedModel || vehicle.dvlaModel || null,
    },
    { label: "Trim", value: vehicle.modelVariantDescription || null },
    { label: "Mileage", value: mileage },
    { label: "Body", value: body },
    { label: "Fuel", value: fuel },
    {
      label: "Engine Code",
      value: vehicle.engineModelCode ? (
        <CopyText text={vehicle.engineModelCode} />
      ) : null,
    },
    { label: "Transmission", value: transmission },
    { label: "BHP", value: bhp },
  ].filter((f) => f.value !== null && f.value !== "");

  const mileageIdx = fields.findIndex((f) => f.label === "Mileage");
  const splitAt =
    mileageIdx === -1 ? Math.ceil(fields.length / 2) : mileageIdx + 1;
  const primaryFields = fields.slice(0, splitAt);
  const secondaryFields = fields.slice(splitAt);

  const renderFieldList = (list: typeof fields) => (
    <dl className="grid min-w-0 grid-cols-[auto,1fr] gap-x-2 gap-y-1 text-xs">
      {list.map((f) => (
        <Fragment key={f.label}>
          <dt className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
            {f.label}
          </dt>
          <dd className="min-w-0 truncate text-xs font-semibold text-slate-800">
            {f.value}
          </dd>
        </Fragment>
      ))}
    </dl>
  );

  // Short headline summary for the card header strip, e.g. "2018 · Ford · Focus".
  const headlineBits = [
    year ? String(year) : null,
    vehicle.combinedMake || vehicle.dvlaMake || null,
    vehicle.combinedModel || vehicle.dvlaModel || null,
  ].filter(Boolean) as string[];
  const headline = headlineBits.length > 0 ? headlineBits.join(" · ") : null;

  return (
    <RowCard
      title="Vehicle Info"
      icon={CarIcon}
      aside={
        headline ? (
          <span className="inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
            {headline}
          </span>
        ) : null
      }
    >
      {/* Definition list — two balanced columns */}
      <div className="flex min-w-0 gap-4 border-t border-slate-100 pt-2">
        {primaryFields.length > 0 && renderFieldList(primaryFields)}
        {secondaryFields.length > 0 && renderFieldList(secondaryFields)}
      </div>
    </RowCard>
  );
}
